/**
 * Analytics tracking utilities for GA4 events
 * @docs https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

/**
 * Track a custom event to Google Analytics
 */
export function trackEvent(eventName: string, params?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

/**
 * Track begin checkout event (GA4 ecommerce)
 */
export function trackBeginCheckout(item: {
  product_id: string;
  price: number;
  currency: string;
}) {
  trackEvent('begin_checkout', {
    currency: item.currency,
    value: item.price / 100, // Convert cents to dollars
    items: [
      {
        item_id: item.product_id,
        item_name: item.product_id,
        price: item.price / 100,
        quantity: 1,
      },
    ],
  });
}

/**
 * Track purchase/conversion event (GA4 ecommerce)
 */
export function trackPurchase(order: {
  order_no: string;
  product_id: string;
  amount: number;
  currency: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}) {
  trackEvent('purchase', {
    transaction_id: order.order_no,
    currency: order.currency,
    value: order.amount / 100,
    // Custom dimensions for attribution
    utm_source: order.utm_source,
    utm_medium: order.utm_medium,
    utm_campaign: order.utm_campaign,
    items: [
      {
        item_id: order.product_id,
        item_name: order.product_id,
        price: order.amount / 100,
        quantity: 1,
      },
    ],
  });
}

/**
 * Track image generation event
 */
export function trackImageGeneration(params: {
  model: string;
  credits_used: number;
  scene: string;
}) {
  trackEvent('generate_image', {
    model: params.model,
    credits_used: params.credits_used,
    scene: params.scene,
  });
}

/**
 * Track free example image generation event
 */
export function trackFreeExampleGeneration(params: {
  model: string;
  scene: string;
}) {
  trackEvent('generate_free_example', {
    model: params.model,
    scene: params.scene,
    is_free: true,
    credits_used: 0,
  });
}

/**
 * Track video generation event
 */
export function trackVideoGeneration(params: {
  model: string;
  credits_used: number;
  scene: string;
}) {
  trackEvent('generate_video', {
    model: params.model,
    credits_used: params.credits_used,
    scene: params.scene,
  });
}

/**
 * Track photo effects generation event
 */
export function trackPhotoEffects(params: {
  effect: string;
  credits_used: number;
}) {
  trackEvent('generate_photo_effect', {
    effect: params.effect,
    credits_used: params.credits_used,
  });
}

/**
 * Track video effects generation event
 */
export function trackVideoEffects(params: {
  effect: string;
  model: string;
  credits_used: number;
}) {
  trackEvent('generate_video_effect', {
    effect: params.effect,
    model: params.model,
    credits_used: params.credits_used,
  });
}

/**
 * Track user sign up event
 */
export function trackSignUp(method: string) {
  trackEvent('sign_up', {
    method: method, // 'google', 'github', 'email', etc.
  });
}

/**
 * Track user login event
 */
export function trackLogin(method: string) {
  trackEvent('login', {
    method: method,
  });
}

/**
 * Track daily checkin event
 */
export function trackDailyCheckin(params: {
  credits_earned: number;
  consecutive_days: number;
  success: boolean;
  error_message?: string;
}) {
  trackEvent('daily_checkin', {
    credits_earned: params.credits_earned,
    consecutive_days: params.consecutive_days,
    success: params.success,
    error_message: params.error_message,
  });
}

/**
 * Track image-to-image generator page view
 */
export function trackImageToImagePageView(params?: {
  default_effect?: string;
  page_path?: string;
}) {
  trackEvent('image_to_image_page_view', {
    default_effect: params?.default_effect,
    page_path: params?.page_path,
  });
}

/**
 * Track effect selection in image-to-image generator
 */
export function trackEffectSelection(params: {
  effect_id: string;
  effect_name: string;
  is_custom: boolean;
}) {
  trackEvent('select_effect', {
    effect_id: params.effect_id,
    effect_name: params.effect_name,
    is_custom: params.is_custom,
  });
}

/**
 * Track image upload in image-to-image generator
 */
export function trackImageUpload(params: {
  image_count: number;
  total_size_mb?: number;
}) {
  trackEvent('upload_image', {
    image_count: params.image_count,
    total_size_mb: params.total_size_mb,
  });
}

/**
 * Track generate button click in image-to-image generator
 */
export function trackGenerateClick(params: {
  effect_id: string;
  effect_name: string;
  is_custom: boolean;
  has_custom_prompt: boolean;
  credits_cost: number;
  remaining_credits: number;
}) {
  trackEvent('click_generate', {
    effect_id: params.effect_id,
    effect_name: params.effect_name,
    is_custom: params.is_custom,
    has_custom_prompt: params.has_custom_prompt,
    credits_cost: params.credits_cost,
    remaining_credits: params.remaining_credits,
  });
}

/**
 * Track checkin dialog open
 */
export function trackCheckinDialogOpen(params?: {
  source?: string; // 'image_to_image', 'homepage', etc.
}) {
  trackEvent('open_checkin_dialog', {
    source: params?.source || 'unknown',
  });
}

/**
 * Track promo banner view
 */
export function trackPromoBannerView(params?: {
  page_path?: string;
  banner_type?: string;
}) {
  trackEvent('view_promo_banner', {
    page_path: params?.page_path,
    banner_type: params?.banner_type || 'inline',
  });
}

/**
 * Track promo banner click
 */
export function trackPromoBannerClick(params?: {
  page_path?: string;
  banner_type?: string;
  destination?: string;
}) {
  trackEvent('click_promo_banner', {
    page_path: params?.page_path,
    banner_type: params?.banner_type || 'inline',
    destination: params?.destination || '/pricing',
  });
}
